import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { teamMembers as fallbackMembers } from "@/data/team";
import { useTranslation } from "@/i18n";

const API = `/api`;

export default function Team() {
  const [members, setMembers] = useState(fallbackMembers);
  const { t } = useTranslation();

  useEffect(() => {
    fetch(`${API}/team`)
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) setMembers(data);
      })
      .catch(() => {});
  }, []);

  return (
    <section id="equipe" className="relative py-24 sm:py-32 lg:py-40 bg-ink" data-testid="team-section">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-12 lg:px-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="flex items-end justify-between flex-wrap gap-8"
        >
          <div>
            <span className="label-accent" data-testid="team-overline">{t("team.overline")}</span>
            <h2 className="mt-6 font-serif font-light tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-tight">
              {t("team.h2a")}
              <em className="italic text-[#A0A5B1]">{t("team.h2b")}</em>
            </h2>
          </div>
          <p className="max-w-sm text-sm text-[#9BA1B0] leading-relaxed">
            {t("team.side")}
          </p>
        </motion.div>

        <div className="mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {members.map((m, i) => (
            <motion.article
              key={m.name}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, delay: i * 0.15, ease: [0.22, 1, 0.36, 1] }}
              className="group"
              data-testid={`team-member-${i}`}
            >
              <Link to={`/equipe/${m.slug}`} data-testid={`team-link-${m.slug}`} className="block">
                <div className="relative overflow-hidden border border-white/5 group-hover:border-gold/40 transition-colors duration-500">
                  <div className="aspect-[3/4] overflow-hidden">
                    <img
                      src={m.image}
                      alt={m.name}
                      loading="lazy"
                      className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-transparent to-transparent opacity-80" />
                  <span className="absolute top-4 right-4 font-mono text-[10px] tracking-[0.25em] text-gold/80 uppercase">
                    {t("team.city")}
                  </span>
                </div>
                <div className="mt-6">
                  <h3 className="font-serif text-xl sm:text-2xl text-[#FAF8F5] group-hover:text-gold-light transition-colors duration-300">{m.name}</h3>
                  <p className="mt-2 text-xs uppercase tracking-[0.15em] text-gold/80">{t(`roles.${m.role}`)}</p>
                  <p className="mt-3 text-[10px] uppercase tracking-[0.2em] text-[#9BA1B0] group-hover:text-gold transition-colors duration-300">
                    {t("team.viewProfile")}
                  </p>
                </div>
              </Link>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
